import type { ClientAccountResponse } from '@api'
import { HStack, useBreakpointValue } from '@chakra-ui/react'
import { shouldShareSystemAudio } from '@handlers/sessions/util'
import { useAudioCapture, useExperienceManager, usePanelManager } from '@hooks'
import { trackEvent } from '@lib/clinician-tracking'
import React, { useEffect } from 'react'
import { isMobile, isSafari } from 'react-device-detect'
import {
  isChromiumBrowser,
  isWindows
} from '../../../../context/sessions-context/utils'
import { FreeTierSessionLimitReached } from '../free-tier-session-limit-reached'
import { MicrophonePermissionsModal } from '../mic-permissions-modal'
import { MicrophoneBlockedWarning } from '../microphone-blocked-warning'
import { NotetakerSettings } from './notetaker-settings'
import { RecordingPanel } from './recording-panel'

type AINotetakerPanelV2Props = {
  sessionId: string
  hasStartedRecording: boolean
  startRecording: () => void
  isRecordingLoading: boolean
  recordingDuration: number
  isDictation: boolean
  recordingStartedAt: string | null | undefined
  onOpenEndModal: () => void
  client: ClientAccountResponse | undefined
  showRecordingLengthWarning: boolean
}

export const AINotetakerPanelV2: React.FC<AINotetakerPanelV2Props> = ({
  hasStartedRecording,
  startRecording,
  isRecordingLoading,
  recordingDuration,
  isDictation,
  recordingStartedAt,
  onOpenEndModal,
  client,
  showRecordingLengthWarning
}) => {
  const {
    documentationAutomationFreeTierSessionLimitReached,
    usageBasedPricingEnabled
  } = useExperienceManager()

  const {
    audioInputs,
    isTelehealth,
    setIsTelehealth,
    isUsingHeadphones,
    isTabAudioStreamOpen,
    isMuted,
    setIsUsingHeadphones,
    testAudioInputs,
    permissionStatus,
    isStopRecordingAfterEnabled,
    stopRecordingAfter,
    setIsStopRecordingAfterEnabled,
    setStopRecordingAfter,
    startContentShare,
    telehealthAudioSource,
    qualityToggle,
    promptForDevicePermissions,
    setActiveWarning,
    isTelehealthSourceRequiredEnabled
  } = useAudioCapture()

  const { isMobileAssistOpen } = usePanelManager()
  const needAudioSharing = shouldShareSystemAudio({
    isWindows: isWindows(),
    isChromium: isChromiumBrowser(),
    isTelehealth: isTelehealth ?? false,
    isUsingHeadphones,
    isMobile,
    telehealthAudioSource,
    qualityToggle,
    isTelehealthSourceRequiredEnabled
  })

  const showSafariWarning =
    isSafari && isTelehealth && !isMobile && !isDictation

  const isMobileBreakpoint = useBreakpointValue({ base: true, md: false })

  const [showPermissionModal, setShowPermissionModal] = React.useState(false)

  useEffect(() => {
    if (permissionStatus === 'unknown') {
      setShowPermissionModal(true)
    }
  }, [permissionStatus])

  const handlePermissionModalClose = async () => {
    setShowPermissionModal(false)
    await promptForDevicePermissions()
  }

  const handleSessionTypeChange = (isTelehealth: boolean) => {
    if (!isTelehealth) {
      setIsUsingHeadphones(false)
    }
    setIsTelehealth(isTelehealth)
    trackEvent('Confirm Audio Settings Dialog -> Changed Setting Input', {
      newSettingSelection: isTelehealth ? 'telehealth' : 'in_person'
    })
  }

  useEffect(() => {
    if (
      (isTelehealth === null || isTelehealth === undefined) &&
      client?.is_demo
    ) {
      handleSessionTypeChange(false)
    }
  }, [isTelehealth, client])

  const handleStartRecording = async () => {
    if (needAudioSharing) {
      const shareResult = await startContentShare()
      if (shareResult !== 'success') {
        return
      }
    }
    window.scrollTo(0, 0)
    setActiveWarning(null)
    startRecording()
  }

  useEffect(() => {
    testAudioInputs()
  }, [testAudioInputs])

  useEffect(() => {
    // Telehealth not possible on mobile, and we hide the option
    if (isMobile) {
      setIsTelehealth(false)
    }
  }, [isMobile])

  if (
    documentationAutomationFreeTierSessionLimitReached &&
    !usageBasedPricingEnabled
  ) {
    return <FreeTierSessionLimitReached onUpgrade={startRecording} />
  }

  if (audioInputs.length === 0 && permissionStatus === 'denied') {
    return <MicrophoneBlockedWarning />
  }

  if (hasStartedRecording) {
    return (
      <RecordingPanel
        isMobileBreakpoint={isMobileBreakpoint ?? false}
        isMuted={isMuted}
        isTabAudioStreamOpen={isTabAudioStreamOpen}
        recordingDuration={recordingDuration}
        isStopRecordingAfterEnabled={isStopRecordingAfterEnabled}
        recordingStartedAt={recordingStartedAt ?? ''}
        onOpenEndModal={onOpenEndModal}
        showPermissionModal={showPermissionModal}
        handlePermissionModalClose={handlePermissionModalClose}
        startContentShare={startContentShare}
        isDictation={isDictation}
        showRecordingLengthWarning={showRecordingLengthWarning}
      />
    )
  }

  return (
    <HStack
      justifyContent="center"
      w="100%"
      px={{ base: 4, sm: 0 }}
      data-testid="ai-notetaker-panel-hstack"
      mb={{ base: '40px', sm: '80px' }}
    >
      <NotetakerSettings
        hasStartedRecording={hasStartedRecording}
        recordingDuration={recordingDuration}
        isDictation={isDictation}
        isMobileAssistOpen={isMobileAssistOpen}
        isStopRecordingAfterEnabled={isStopRecordingAfterEnabled}
        setIsStopRecordingAfterEnabled={setIsStopRecordingAfterEnabled}
        stopRecordingAfter={stopRecordingAfter}
        setStopRecordingAfter={setStopRecordingAfter}
        needAudioSharing={needAudioSharing}
        showSafariWarning={showSafariWarning ?? false}
        isRecordingLoading={isRecordingLoading}
        handleStartRecording={handleStartRecording}
        client={client ?? null}
      />
      <MicrophonePermissionsModal
        isOpen={showPermissionModal}
        onClose={handlePermissionModalClose}
      />
    </HStack>
  )
}
